import axios from 'axios';
import { getApiUrl } from './apiBase';

const API_BASE = getApiUrl();

const getToken = () => localStorage.getItem('token') || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const asId = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  const obj = value as { _id?: string; id?: string };
  return obj._id || obj.id || '';
};

export interface Conversation {
  conversationId: string;
  lastMessage: {
    _id: string;
    content: string;
    sender: string;
    read: boolean;
    createdAt: string;
  };
  unreadCount: number;
  otherParty: {
    _id: string;
    firstName: string;
    lastName: string;
    role?: string;
  };
}

export interface MessageItem {
  _id: string;
  sender: string;
  recipient: string;
  furniture?: string;
  order?: string;
  content: string;
  read: boolean;
  createdAt: string;
}

const normalizeMessage = (msg: any): MessageItem => ({
  _id: asId(msg._id) || msg._id,
  sender: asId(msg.sender),
  recipient: asId(msg.recipient),
  furniture: msg.furniture ? asId(msg.furniture) : undefined,
  order: msg.order ? asId(msg.order) : undefined,
  content: msg.content,
  read: Boolean(msg.read),
  createdAt: msg.createdAt,
});

export const getConversations = async (page = 1, limit = 20) => {
  const { data } = await api.get(`/messages/conversations?page=${page}&limit=${limit}`);
  const list = Array.isArray(data) ? data : data.conversations || [];
  return list as Conversation[];
};

export const getConversationMessages = async (conversationId: string, page = 1, limit = 50) => {
  const { data } = await api.get(`/messages/${conversationId}?page=${page}&limit=${limit}`);
  const list = Array.isArray(data) ? data : data.messages || [];
  return (list as any[]).map(normalizeMessage);
};

export const sendMessage = async (data: {
  recipient: string;
  furniture?: string;
  order?: string;
  content: string;
}) => {
  const { data: result } = await api.post('/messages', {
    recipient: data.recipient,
    recipientId: data.recipient,
    furniture: data.furniture,
    furnitureId: data.furniture,
    order: data.order,
    orderId: data.order,
    content: data.content,
  });
  return normalizeMessage(result);
};

export const markAsRead = async (messageId: string) => {
  const { data } = await api.patch(`/messages/${messageId}/read`);
  return data;
};

export const reportConversation = async (conversationId: string, reason: string) => {
  const { data } = await api.post('/reports', { conversationId, reason });
  return data;
};
